import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, MenuItem } from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import styles from './styles.module.scss';

import { apiPreviewMail, apiSendCandidateMail } from 'api/mailTemplate';
import { useGetListMailTemplate, useGetMailRecipient } from 'hook/useMailTemplate';

import { HTTP_OK, HTTP_UNPROCESSABLE_ENTITY, HTTP_EXPECTATION_FAILED } from 'constants/StatusCode.js';
import { MAX_LENGTH_255 } from 'constants/index.js';
import { USE_GET_MAIL_LOGS, MAIL_ACTIVE } from 'constants/MailTemplate';

import ModalComponent from 'components/Modal';
import ButtonComponent from 'components/Buttons';
import CustomInputYup from 'components/InputYupAndMui/CustomInputYup';
import { CssTextField } from 'components/InputYupAndMui/constant';
import MailEditor from 'components/MailEditor';
import MailPreviewModal from 'components/MailEditor/MailPreviewModal';

import { showAlert } from 'helpers/modal';

const MAX_TEMPLATE_OPTION = 100;
const NO_TEMPLATE = '';

const EMAIL_REGEX = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;

const isEmailList = (value) =>
  !value ||
  value
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean)
    .every((email) => EMAIL_REGEX.test(email));

const ModalSendMail = ({ isShowModal, setShowModal, applyId, candidateEmail, onSuccess }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState(null);

  const { data: dataListTemplate } = useGetListMailTemplate({ is_active: MAIL_ACTIVE, per_page: MAX_TEMPLATE_OPTION });
  const listTemplate = dataListTemplate?.data || [];
  const { data: recipient } = useGetMailRecipient(applyId, { enabled: !!applyId && isShowModal });

  const {
    handleSubmit,
    control,
    register,
    setValue,
    setError,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        subject: Yup.string().trim().required(t('managerMailTemplate.subjectRequired')),
        body: Yup.string().required(t('managerMailTemplate.bodyRequired')),
        cc: Yup.string().nullable().test('emails', t('managerMailTemplate.emailListInvalid'), isEmailList),
        bcc: Yup.string().nullable().test('emails', t('managerMailTemplate.emailListInvalid'), isEmailList),
      }),
    ),
    defaultValues: { template_id: NO_TEMPLATE, subject: '', body: '', cc: '', bcc: '' },
  });

  const templateId = useWatch({ control, name: 'template_id' });

  const getPayload = () => {
    const values = getValues();
    return {
      template_id: values.template_id || null,
      subject: values.subject,
      body: values.body,
      cc: values.cc,
      bcc: values.bcc,
    };
  };

  // Lấy CC/BCC mặc định của mẫu (đã gồm HR phụ trách nếu mẫu bật is_cc_hr)
  const { mutate: loadDefaultRecipient } = useMutation(
    (id) => apiPreviewMail({ apply_id: applyId, template_id: id }),
    {
      onSuccess: (response) => {
        if (response?.data?.status === HTTP_OK && response?.data?.data) {
          setValue('cc', response.data.data.cc || '');
          setValue('bcc', response.data.data.bcc || '');
        }
      },
    },
  );

  const handleChangeTemplate = (id) => {
    setValue('template_id', id);
    const template = listTemplate.find((item) => item.id === id);
    // Điền nội dung gốc (còn placeholder), BE render lúc gửi; HR sửa thoải mái trước khi gửi
    setValue('subject', template?.subject || '');
    setValue('body', template?.body || '');
    setValue('cc', template?.cc || '');
    setValue('bcc', template?.bcc || '');
    clearErrors();
    if (id) loadDefaultRecipient(id);
  };

  const { mutate: previewMail, isLoading: isLoadingPreview } = useMutation(
    () => apiPreviewMail({ apply_id: applyId, ...getPayload() }),
    {
      onSuccess: (response) => {
        if (response?.data?.status === HTTP_OK && response?.data?.data) {
          // Hiện đúng CC/BCC HR đang nhập thay vì mặc định của mẫu
          const { cc, bcc } = getValues();
          setPreview({ ...response.data.data, cc, bcc });
        } else {
          showAlert(t('managerMailTemplate.previewFail'));
        }
      },
      onError: () => {
        showAlert(t('messages.systemError'));
      },
    },
  );

  const { mutate: sendMail, isLoading: isLoadingSend } = useMutation(
    () => apiSendCandidateMail(applyId, getPayload()),
    {
      onSuccess: (response) => {
        const status = response?.data?.status;
        if (status === HTTP_UNPROCESSABLE_ENTITY) {
          const messages = response?.data?.messages || {};
          Object.keys(messages).forEach((key) => setError(key, { message: messages[key]?.[0] }));
          return;
        }
        // Gửi lỗi BE vẫn ghi log FAILED -> luôn làm mới lịch sử
        queryClient.invalidateQueries([USE_GET_MAIL_LOGS, applyId]);
        if (status === HTTP_OK) {
          setShowModal(false);
          onSuccess(t('candidateMail.sendSuccess'));
        } else if (status === HTTP_EXPECTATION_FAILED) {
          setShowModal(false);
          onSuccess(response?.data?.message || t('candidateMail.sendFail'));
        } else {
          showAlert(response?.data?.message || t('messages.systemError'));
        }
      },
      onError: () => {
        showAlert(t('messages.systemError'));
      },
    },
  );

  return (
    <>
      <ModalComponent
        toggle={() => setShowModal(false)}
        isShowModal={isShowModal}
        title={t('candidateMail.sendMail')}
        classNameAdd={styles.modalSendMail}
      >
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CssTextField
                select
                fullWidth
                label={t('candidateMail.template')}
                value={templateId}
                onChange={(event) => handleChangeTemplate(event.target.value)}
              >
                <MenuItem value={NO_TEMPLATE}>{t('candidateMail.noTemplate')}</MenuItem>
                {listTemplate.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name}
                  </MenuItem>
                ))}
              </CssTextField>
            </Grid>
            <Grid item xs={12} className={styles.toEmail}>
              <b>{t('candidateMail.to')}:</b>{' '}
              {candidateEmail || <span className={styles.noEmail}>{t('candidateMail.noEmail')}</span>}
            </Grid>
            {recipient?.dry_run && (
              <Grid item xs={12}>
                <div className={styles.dryRunWarning}>
                  {recipient.dry_run_to
                    ? t('candidateMail.dryRunWarning', { email: recipient.dry_run_to })
                    : t('candidateMail.dryRunNoRecipient')}
                </div>
              </Grid>
            )}
            <Grid item xs={6}>
              <CustomInputYup
                label={t('managerMailTemplate.cc')}
                name="cc"
                control={control}
                register={register}
                errors={errors}
                useWatch={useWatch}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomInputYup
                label={t('managerMailTemplate.bcc')}
                name="bcc"
                control={control}
                register={register}
                errors={errors}
                useWatch={useWatch}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomInputYup
                label={t('managerMailTemplate.subject')}
                name="subject"
                control={control}
                register={register}
                errors={errors}
                useWatch={useWatch}
                required
                maxLength={MAX_LENGTH_255}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="body"
                control={control}
                render={({ field }) => (
                  <MailEditor value={field.value} onChange={field.onChange} error={errors.body?.message} />
                )}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} className="btn-submit mt-3">
            <Grid item xs={4}>
              <ButtonComponent
                text={t('common.cancel')}
                addClass="btn-bg-yellow2 w-100"
                handleClick={() => setShowModal(false)}
              />
            </Grid>
            <Grid item xs={4}>
              <ButtonComponent
                text={t('candidateMail.preview')}
                addClass="btn-bg-yellow2 w-100"
                handleClick={handleSubmit(() => previewMail())}
                isLoading={isLoadingPreview}
              />
            </Grid>
            <Grid item xs={4}>
              <ButtonComponent
                text={t('candidateMail.send')}
                addClass="btn-bg-yellow1 w-100"
                hasDisabled={!candidateEmail}
                type="submit"
                handleClick={handleSubmit(() => sendMail())}
                isLoading={isLoadingSend}
              />
            </Grid>
          </Grid>
        </form>
      </ModalComponent>
      {!!preview && <MailPreviewModal isShowModal={!!preview} setShowModal={() => setPreview(null)} preview={preview} />}
    </>
  );
};

export default ModalSendMail;
